import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Check,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Copy,
  CreditCard,
  Download,
  Eye,
  FilePenLine,
  Heart,
  Image as ImageIcon,
  LayoutDashboard,
  Menu,
  Minus,
  Package,
  PackageCheck,
  PanelLeftClose,
  Plus,
  RotateCcw,
  Search,
  Settings2,
  ShoppingBag,
  SlidersHorizontal,
  Sparkles,
  Store,
  Tag,
  Trash2,
  TrendingUp,
  Truck,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import {
  CSSProperties,
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import gsap from "gsap";
import { useLocation, useNavigate } from "react-router-dom";
import { ConceptBar } from "../components/ConceptBar";
import {
  emptyStoreState,
  StoreBanner,
  StoreAudience,
  StoreCategory,
  StoreOrder,
  StoreProduct,
  StoreState,
  storeCategories,
} from "../data/sloiStore";
import { sloiApi, StoreApiError } from "../data/sloiApi";
import { useGsap } from "../hooks/useGsap";
import "../styles/sloi-store.css";

type StoreView = "shop" | "account" | "admin";
type AdminSection = "overview" | "products" | "orders" | "content";
type SortMode = "featured" | "price-asc" | "price-desc" | "rating";

const categoryRoutes: Array<{ slug: string; name: StoreCategory; description: string }> = [
  { slug: "outerwear", name: "Верхняя одежда", description: "Пальто и технологичные внешние слои для города" },
  { slug: "knitwear", name: "Трикотаж", description: "Меринос, мягкая база и тонкие слои" },
  { slug: "tops", name: "Топы", description: "Майки, сетка и архитектурный крой" },
  { slug: "trousers", name: "Брюки", description: "Деним, карго и широкий силуэт" },
  { slug: "shoes", name: "Обувь", description: "Пары, рассчитанные на городской ритм" },
];

const audienceRoutes: Array<{ slug: string; name: StoreAudience; title: string }> = [
  { slug: "women", name: "Женское", title: "Женская коллекция" },
  { slug: "men", name: "Мужское", title: "Мужская коллекция" },
  { slug: "unisex", name: "Унисекс", title: "Унисекс" },
];

const storeImageLibrary = Array.from(
  { length: 13 },
  (_, index) => `/assets/vanta/look-${String(index + 1).padStart(2, "0")}.jpg`,
);

type CartLine = {
  productId: string;
  size: string;
  quantity: number;
};

const CART_KEY = "vivor:sloi-cart:v1";
const FAVORITES_KEY = "vivor:sloi-favorites:v1";
const orderStatuses: StoreOrder["status"][] = [
  "Новый",
  "Подтверждён",
  "Собирается",
  "Передан в доставку",
  "Завершён",
  "Отменён",
];

function readJson<T>(key: string, fallback: T): T {
  try {
    const value = window.localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
}

function formatMoney(value: number) {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function EmptyState({
  icon,
  title,
  text,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="sloi-empty">
      <span>{icon}</span>
      <h3>{title}</h3>
      <p>{text}</p>
      {action}
    </div>
  );
}

function ProductCard({
  product,
  favorite,
  onFavorite,
  onOpen,
}: {
  product: StoreProduct;
  favorite: boolean;
  onFavorite: () => void;
  onOpen: () => void;
}) {
  return (
    <article className="sloi-product" data-product-card data-spotlight>
      <button
        className={`sloi-product__favorite ${favorite ? "is-active" : ""}`}
        type="button"
        aria-label={favorite ? "Удалить из избранного" : "Добавить в избранное"}
        aria-pressed={favorite}
        onClick={onFavorite}
      >
        <Heart size={18} fill={favorite ? "currentColor" : "none"} aria-hidden="true" />
      </button>
      <button className="sloi-product__media" type="button" onClick={onOpen}>
        <img src={product.image} alt={`${product.name}, ${product.color}`} width="760" height="960" loading="lazy" />
        {product.badge && <span>{product.badge}</span>}
        <i aria-hidden="true">Быстрый просмотр <ArrowUpRight size={16} /></i>
      </button>
      <div className="sloi-product__meta">
        <span>{product.brand} · {product.sku}</span>
        <span>{product.stock} шт.</span>
      </div>
      <button className="sloi-product__title" type="button" onClick={onOpen}>
        <strong>{product.name}</strong>
        <span>{product.color}</span>
      </button>
      <div className="sloi-product__price">
        <b>{formatMoney(product.price)}</b>
        {product.oldPrice && <del>{formatMoney(product.oldPrice)}</del>}
      </div>
    </article>
  );
}

function ProductDrawer({
  product,
  favorite,
  onClose,
  onFavorite,
  onAdd,
}: {
  product: StoreProduct;
  favorite: boolean;
  onClose: () => void;
  onFavorite: () => void;
  onAdd: (size: string) => void;
}) {
  const firstAvailable = product.variants.find((variant) => variant.stock > 0)?.size ?? product.sizes[0];
  const [size, setSize] = useState(firstAvailable);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  return (
    <div className="sloi-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="sloi-product-drawer" role="dialog" aria-modal="true" aria-labelledby="sloi-product-title">
        <button ref={closeRef} className="sloi-icon-button sloi-product-drawer__close" type="button" onClick={onClose} aria-label="Закрыть карточку товара">
          <X aria-hidden="true" />
        </button>
        <div className="sloi-product-drawer__image">
          <img src={product.image} alt={`${product.name}, ${product.color}`} width="920" height="1200" />
          <span>{product.sku}</span>
        </div>
        <div className="sloi-product-drawer__body">
          <p className="sloi-kicker">{product.brand} · {product.category}</p>
          <h2 id="sloi-product-title">{product.name}</h2>
          <p className="sloi-product-drawer__description">{product.description}</p>
          <div className="sloi-product-drawer__rating">
            <span>{product.sku}</span>
            <span>{product.stock > 6 ? "В наличии" : `Осталось ${product.stock}`}</span>
          </div>
          <div className="sloi-product-drawer__price">
            <strong>{formatMoney(product.price)}</strong>
            {product.oldPrice && <del>{formatMoney(product.oldPrice)}</del>}
          </div>
          <div className="sloi-size-picker">
            <div>
              <span>Размер</span>
              <button type="button" onClick={() => setSizeGuideOpen(!sizeGuideOpen)} aria-expanded={sizeGuideOpen}>Таблица размеров <ArrowUpRight size={14} /></button>
            </div>
            <div>
              {product.variants.map((variant) => (
                <button
                  className={variant.size === size ? "is-active" : ""}
                  type="button"
                  key={variant.size}
                  aria-pressed={variant.size === size}
                  disabled={variant.stock === 0}
                  title={variant.stock === 0 ? "Размер закончился" : `В наличии: ${variant.stock}`}
                  onClick={() => setSize(variant.size)}
                >
                  {variant.size}<small>{variant.stock}</small>
                </button>
              ))}
            </div>
            {sizeGuideOpen && (
              <div className="sloi-size-guide">
                <strong>Как выбрать размер</strong>
                <p>{product.fit}</p>
                <div><span>XS / 42</span><span>S / 44</span><span>M / 46</span><span>L / 48</span><span>XL / 50</span></div>
              </div>
            )}
          </div>
          <dl className="sloi-product-drawer__details">
            <div><dt>Цвет</dt><dd><i style={{ background: product.colorHex }} /> {product.color}</dd></div>
            <div><dt>Состав</dt><dd>{product.material}</dd></div>
            <div><dt>Посадка</dt><dd>{product.fit}</dd></div>
            <div><dt>Уход</dt><dd>{product.care}</dd></div>
            <div><dt>Доставка</dt><dd>Курьером или в пункт выдачи; точная стоимость рассчитывается в заказе</dd></div>
          </dl>
          <div className="sloi-product-drawer__actions">
            <button className="sloi-primary-button" type="button" onClick={() => onAdd(size)}>
              Добавить в корзину <ShoppingBag size={17} />
            </button>
            <button className={`sloi-icon-button ${favorite ? "is-active" : ""}`} type="button" onClick={onFavorite} aria-label="Переключить избранное">
              <Heart fill={favorite ? "currentColor" : "none"} aria-hidden="true" />
            </button>
          </div>
          <p className="sloi-product-drawer__service">Бесплатная примерка · возврат в течение 14 дней · оплата после примерки</p>
        </div>
      </section>
    </div>
  );
}

function CartDrawer({
  products,
  cart,
  onClose,
  onQuantity,
  onRemove,
  onCheckout,
}: {
  products: StoreProduct[];
  cart: CartLine[];
  onClose: () => void;
  onQuantity: (index: number, next: number) => void;
  onRemove: (index: number) => void;
  onCheckout: () => void;
}) {
  const items = cart.map((line, index) => ({
    line,
    index,
    product: products.find((product) => product.id === line.productId),
  })).filter((item): item is typeof item & { product: StoreProduct } => Boolean(item.product));
  const total = items.reduce((sum, item) => sum + item.product.price * item.line.quantity, 0);

  return (
    <div className="sloi-overlay sloi-overlay--right" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <aside className="sloi-cart" role="dialog" aria-modal="true" aria-labelledby="sloi-cart-title">
        <header>
          <div>
            <p className="sloi-kicker">Ваш выбор</p>
            <h2 id="sloi-cart-title">Корзина <span>{cart.reduce((sum, item) => sum + item.quantity, 0)}</span></h2>
          </div>
          <button className="sloi-icon-button" type="button" onClick={onClose} aria-label="Закрыть корзину"><X /></button>
        </header>
        {items.length ? (
          <>
            <div className="sloi-cart__items">
              {items.map(({ line, product, index }) => (
                <article className="sloi-cart__item" key={`${line.productId}-${line.size}`}>
                  <img src={product.image} alt="" width="180" height="220" />
                  <div>
                    <span>{product.brand}</span>
                    <strong>{product.name}</strong>
                    <p>Размер {line.size} · {product.color}</p>
                    <div className="sloi-cart__quantity">
                      <button type="button" aria-label="Уменьшить количество" onClick={() => onQuantity(index, line.quantity - 1)}><Minus /></button>
                      <span>{line.quantity}</span>
                      <button type="button" aria-label="Увеличить количество" onClick={() => onQuantity(index, line.quantity + 1)}><Plus /></button>
                    </div>
                  </div>
                  <div>
                    <b>{formatMoney(product.price * line.quantity)}</b>
                    <button type="button" onClick={() => onRemove(index)}>Удалить</button>
                  </div>
                </article>
              ))}
            </div>
            <div className="sloi-cart__benefit">
              <Truck aria-hidden="true" />
              <div>
                <strong>{total >= 25000 ? "Доставка за наш счёт" : `До бесплатной доставки ${formatMoney(25000 - total)}`}</strong>
                <span><i style={{ "--delivery-progress": `${Math.min(100, total / 250)}%` } as CSSProperties} /></span>
              </div>
            </div>
            <footer>
              <div><span>Товары</span><b>{formatMoney(total)}</b></div>
              <div><span>Доставка</span><b>{total >= 25000 ? "0 ₽" : "590 ₽"}</b></div>
              <div className="sloi-cart__total"><span>Итого</span><strong>{formatMoney(total + (total >= 25000 ? 0 : 590))}</strong></div>
              <button className="sloi-primary-button" type="button" onClick={onCheckout}>
                Перейти к оформлению <ArrowRight />
              </button>
              <p>Тестовая оплата картой или оплата при получении</p>
            </footer>
          </>
        ) : (
          <EmptyState
            icon={<ShoppingBag />}
            title="Корзина пока пуста"
            text="Добавьте вещь из каталога — она появится здесь вместе с выбранным размером."
            action={<button className="sloi-secondary-button" type="button" onClick={onClose}>Перейти в каталог</button>}
          />
        )}
      </aside>
    </div>
  );
}

function CheckoutDrawer({
  products,
  cart,
  onClose,
  onSubmit,
}: {
  products: StoreProduct[];
  cart: CartLine[];
  onClose: () => void;
  onSubmit: (data: {
    customer: { name: string; email: string; phone: string; city: string; address: string };
    delivery: "courier" | "pickup";
    payment: "card" | "on-receipt";
  }) => Promise<void>;
}) {
  const [delivery, setDelivery] = useState<"courier" | "pickup">("courier");
  const [payment, setPayment] = useState<"card" | "on-receipt">("card");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const total = cart.reduce((sum, line) => {
    const product = products.find((item) => item.id === line.productId);
    return sum + (product?.price ?? 0) * line.quantity;
  }, 0);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setSubmitting(true);
    setFormError("");
    try {
      await onSubmit({
        customer: {
        name: String(data.get("name") ?? ""),
        email: String(data.get("email") ?? ""),
          phone: String(data.get("phone") ?? ""),
        city: String(data.get("city") ?? ""),
          address: String(data.get("address") ?? ""),
        },
        delivery,
        payment,
      });
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Не удалось оформить заказ");
      setSubmitting(false);
    }
  };

  return (
    <div className="sloi-overlay" role="presentation">
      <section className="sloi-checkout" role="dialog" aria-modal="true" aria-labelledby="checkout-title">
        <header>
          <div>
            <p className="sloi-kicker">Оформление · 3 минуты</p>
            <h2 id="checkout-title">Почти готово</h2>
          </div>
          <button className="sloi-icon-button" type="button" onClick={onClose} aria-label="Закрыть оформление"><X /></button>
        </header>
        <form onSubmit={submit}>
          <div className="sloi-checkout__content">
            <fieldset>
              <legend><span>01</span> Контакты</legend>
              <div className="sloi-form-grid">
                <label>
                  <span>Имя и фамилия</span>
                  <input name="name" required autoComplete="name" placeholder="Алексей Морозов" />
                </label>
                <label>
                  <span>Email</span>
                  <input name="email" type="email" required autoComplete="email" placeholder="alex@example.com" />
                </label>
                <label>
                  <span>Телефон</span>
                  <input name="phone" type="tel" required inputMode="tel" autoComplete="tel" placeholder="+7 999 123-45-67" />
                </label>
                <label>
                  <span>Город</span>
                  <input name="city" required autoComplete="address-level2" placeholder="Москва" />
                </label>
                {delivery === "courier" && (
                  <label className="sloi-form-grid__wide">
                    <span>Адрес доставки</span>
                    <input name="address" required autoComplete="street-address" placeholder="Улица, дом, квартира" />
                  </label>
                )}
              </div>
            </fieldset>
            <fieldset>
              <legend><span>02</span> Получение</legend>
              <div className="sloi-choice-grid">
                <button className={delivery === "courier" ? "is-active" : ""} type="button" onClick={() => setDelivery("courier")}>
                  <Truck /><strong>Курьером</strong><span>Ориентировочно 2–4 рабочих дня</span><b>{total >= 25000 ? "Бесплатно" : "590 ₽"}</b>
                </button>
                <button className={delivery === "pickup" ? "is-active" : ""} type="button" onClick={() => setDelivery("pickup")}>
                  <Package /><strong>В пункт SLOI</strong><span>Хлебозавод · Новодмитровская, 1</span><b>Бесплатно</b>
                </button>
              </div>
            </fieldset>
            <fieldset>
              <legend><span>03</span> Оплата</legend>
              <div className="sloi-choice-grid">
                <button className={payment === "card" ? "is-active" : ""} type="button" onClick={() => setPayment("card")}>
                  <CreditCard /><strong>Картой онлайн</strong><span>Тестовая авторизация без списания</span>
                </button>
                <button className={payment === "on-receipt" ? "is-active" : ""} type="button" onClick={() => setPayment("on-receipt")}>
                  <PackageCheck /><strong>После примерки</strong><span>Оплатите только то, что подошло</span>
                </button>
              </div>
            </fieldset>
          </div>
          <aside className="sloi-checkout__summary">
            <p className="sloi-kicker">{cart.length} позиции</p>
            <div className="sloi-checkout__thumbs">
              {cart.slice(0, 4).map((line) => {
                const product = products.find((item) => item.id === line.productId);
                return product ? <img key={`${line.productId}-${line.size}`} src={product.image} alt="" width="100" height="120" /> : null;
              })}
            </div>
            <dl>
              <div><dt>Товары</dt><dd>{formatMoney(total)}</dd></div>
              <div><dt>Доставка</dt><dd>{total >= 25000 || delivery === "pickup" ? "0 ₽" : "590 ₽"}</dd></div>
            </dl>
            <div className="sloi-checkout__total"><span>Итого</span><strong>{formatMoney(total + (total >= 25000 || delivery === "pickup" ? 0 : 590))}</strong></div>
            <p className="sloi-checkout__notice">Это общая демо-среда: не вводите реальные персональные данные. {payment === "card" && "Эквайринг не списывает деньги, но заказ, цена и остатки обрабатываются сервером."}</p>
            {formError && <p className="sloi-checkout__error" role="alert">{formError}</p>}
            <button className="sloi-primary-button" disabled={submitting} type="submit">
              {submitting ? "Создаём заказ…" : "Оформить заказ"} <ArrowRight />
            </button>
            <p>Нажимая кнопку, вы принимаете условия оферты и политики конфиденциальности.</p>
          </aside>
        </form>
      </section>
    </div>
  );
}

function Storefront({
  storeState,
  favorites,
  onFavorite,
  onProduct,
  onCategory,
  onAudience,
  onHome,
  routeCategory,
  routeAudience,
  routeTitle,
  routeDescription,
  routeImage,
}: {
  storeState: StoreState;
  favorites: string[];
  onFavorite: (id: string) => void;
  onProduct: (product: StoreProduct) => void;
  onCategory: (category: StoreCategory) => void;
  onAudience: (audience: StoreAudience) => void;
  onHome: () => void;
  routeCategory?: StoreCategory;
  routeAudience?: StoreAudience;
  routeTitle?: string;
  routeDescription?: string;
  routeImage?: string;
}) {
  const [bannerIndex, setBannerIndex] = useState(0);
  const [query, setQuery] = useState("");
  const [categories, setCategories] = useState<string[]>([]);
  const [audiences, setAudiences] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [sizes, setSizes] = useState<string[]>([]);
  const [maxPrice, setMaxPrice] = useState(40000);
  const [sort, setSort] = useState<SortMode>("featured");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const heroMedia = useRef<HTMLDivElement>(null);
  const heroCopy = useRef<HTMLDivElement>(null);
  const catalogRef = useRef<HTMLElement>(null);
  const banner = storeState.banners[bannerIndex] ?? storeState.banners[0];
  const isCollectionPage = Boolean(routeTitle);

  useEffect(() => {
    setCategories(routeCategory ? [routeCategory] : []);
    setAudiences(routeAudience ? [routeAudience] : []);
    setBrands([]);
    setSizes([]);
    setMaxPrice(40000);
    setQuery("");
    setFiltersOpen(false);
  }, [routeAudience, routeCategory]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setBannerIndex((current) => (current + 1) % storeState.banners.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [storeState.banners.length]);

  useEffect(() => {
    const handleCatalogNavigation = (event: Event) => {
      const detail = (event as CustomEvent<{
        audience?: string;
        query?: string;
        reset?: boolean;
      }>).detail;

      if (detail.reset) resetFilters();
      if (detail.audience) {
        setCategories([]);
        setAudiences([detail.audience]);
        setBrands([]);
        setSizes([]);
      }
      if (typeof detail.query === "string") setQuery(detail.query);

      window.requestAnimationFrame(() => {
        catalogRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    };

    window.addEventListener("sloi:catalog", handleCatalogNavigation);
    return () => window.removeEventListener("sloi:catalog", handleCatalogNavigation);
  }, []);

  useEffect(() => {
    if (!heroMedia.current || !heroCopy.current) return;
    const context = gsap.context(() => {
      gsap.fromTo(heroMedia.current, { autoAlpha: 0.4, scale: 1.045 }, { autoAlpha: 1, scale: 1, duration: 0.9, ease: "expo.out" });
      gsap.fromTo(heroCopy.current?.children ?? [], { y: 28, autoAlpha: 0 }, { y: 0, autoAlpha: 1, stagger: 0.07, duration: 0.72, ease: "power3.out" });
    });
    return () => context.revert();
  }, [bannerIndex]);

  const availableProducts = useMemo(
    () => storeState.products.filter((product) => product.published && product.stock > 0),
    [storeState.products],
  );
  const allBrands = useMemo(
    () => Array.from(new Set(availableProducts.map((product) => product.brand))).sort(),
    [availableProducts],
  );
  const allSizes = useMemo(
    () => Array.from(new Set(
      availableProducts.flatMap((product) => product.variants.filter((variant) => variant.stock > 0).map((variant) => variant.size)),
    )).slice(0, 14),
    [availableProducts],
  );
  const normalizedQuery = query.trim().toLowerCase();

  const matchesFilters = (
    product: StoreProduct,
    ignore?: "category" | "audience" | "brand" | "size" | "price",
  ) => {
    const haystack = `${product.name} ${product.brand} ${product.category} ${product.material} ${product.color} ${product.sku}`.toLowerCase();
    return (
      (!normalizedQuery || haystack.includes(normalizedQuery)) &&
      (ignore === "category" || !categories.length || categories.includes(product.category)) &&
      (ignore === "audience" || !audiences.length || audiences.includes(product.audience)) &&
      (ignore === "brand" || !brands.length || brands.includes(product.brand)) &&
      (ignore === "size" || !sizes.length || sizes.some((size) => product.variants.some((variant) => variant.size === size && variant.stock > 0))) &&
      (ignore === "price" || product.price <= maxPrice)
    );
  };

  const filtered = useMemo(() => {
    const next = availableProducts.filter((product) => matchesFilters(product));
    return next.sort((a, b) => {
      if (sort === "price-asc") return a.price - b.price;
      if (sort === "price-desc") return b.price - a.price;
      if (sort === "rating") return b.rating - a.rating;
      return Number(Boolean(b.badge)) - Number(Boolean(a.badge)) || b.stock - a.stock;
    });
  }, [audiences, availableProducts, brands, categories, maxPrice, normalizedQuery, sizes, sort]);

  const categoryCounts = useMemo(() => Object.fromEntries(
    storeCategories.map((category) => [
      category.name,
      availableProducts.filter((product) => matchesFilters(product, "category") && product.category === category.name).length,
    ]),
  ), [audiences, availableProducts, brands, maxPrice, normalizedQuery, sizes]);
  const audienceCounts = useMemo(() => Object.fromEntries(
    audienceRoutes.map((audience) => [
      audience.name,
      availableProducts.filter((product) => matchesFilters(product, "audience") && product.audience === audience.name).length,
    ]),
  ), [availableProducts, brands, categories, maxPrice, normalizedQuery, sizes]);
  const brandCounts = useMemo(() => Object.fromEntries(
    allBrands.map((brand) => [
      brand,
      availableProducts.filter((product) => matchesFilters(product, "brand") && product.brand === brand).length,
    ]),
  ), [allBrands, audiences, availableProducts, categories, maxPrice, normalizedQuery, sizes]);
  const sizeCounts = useMemo(() => Object.fromEntries(
    allSizes.map((size) => [
      size,
      availableProducts.filter((product) => matchesFilters(product, "size") && product.variants.some((variant) => variant.size === size && variant.stock > 0)).length,
    ]),
  ), [allSizes, audiences, availableProducts, brands, categories, maxPrice, normalizedQuery]);

  const visibleCategoryFilters = categories.filter((category) => category !== routeCategory);
  const visibleAudienceFilters = audiences.filter((audience) => audience !== routeAudience);
  const activeFilters = visibleCategoryFilters.length + visibleAudienceFilters.length + brands.length + sizes.length + (maxPrice < 40000 ? 1 : 0);

  const toggle = (value: string, values: string[], setter: (next: string[]) => void) => {
    setter(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
  };

  const resetFilters = () => {
    setCategories(routeCategory ? [routeCategory] : []);
    setAudiences(routeAudience ? [routeAudience] : []);
    setBrands([]);
    setSizes([]);
    setMaxPrice(40000);
    setQuery("");
  };

  const applyCategory = (category: StoreCategory) => {
    onCategory(category);
  };

  return (
    <>
      {isCollectionPage ? (
        <section className="sloi-collection-hero" aria-labelledby="sloi-collection-title">
          <div className="sloi-collection-hero__media">
            <img src={routeImage} alt="" width="1200" height="1200" />
            <span>{routeCategory ? "Категория" : "Коллекция"} / SLOI</span>
          </div>
          <div className="sloi-collection-hero__copy">
            <nav aria-label="Хлебные крошки">
              <button type="button" onClick={onHome}>Каталог</button>
              <ChevronRight />
              <span>{routeTitle}</span>
            </nav>
            <p className="sloi-kicker">Отдельная страница ассортимента</p>
            <h1 id="sloi-collection-title">{routeTitle}</h1>
            <p>{routeDescription}</p>
            <div>
              <strong>{filtered.length}</strong>
              <span>моделей в наличии<br />с живыми остатками по размеру</span>
            </div>
            <button className="sloi-primary-button" type="button" onClick={() => catalogRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}>
              Смотреть каталог <ArrowDown />
            </button>
          </div>
        </section>
      ) : (
        <>
      <section className={`sloi-hero sloi-hero--${banner.tone}`} aria-labelledby="sloi-hero-title">
        <div className="sloi-hero__media" ref={heroMedia}>
          <img src={banner.image} alt="" width="1500" height="1100" />
          <div className="sloi-hero__scan" aria-hidden="true" />
        </div>
        <div className="sloi-hero__copy" ref={heroCopy}>
          <p>{banner.eyebrow}</p>
          <h1 id="sloi-hero-title">{banner.title}</h1>
          <span>{banner.text}</span>
          <button className="sloi-hero__cta" type="button" onClick={() => banner.category ? applyCategory(banner.category) : catalogRef.current?.scrollIntoView({ behavior: "smooth" })}>
            {banner.cta} <ArrowRight />
          </button>
        </div>
        <div className="sloi-hero__index" aria-hidden="true">
          <span>{String(bannerIndex + 1).padStart(2, "0")}</span>
          <i />
          <span>{String(storeState.banners.length).padStart(2, "0")}</span>
        </div>
        <div className="sloi-hero__controls">
          <button type="button" aria-label="Предыдущий баннер" onClick={() => setBannerIndex((bannerIndex - 1 + storeState.banners.length) % storeState.banners.length)}><ArrowLeft /></button>
          <div>
            {storeState.banners.map((item, index) => (
              <button
                className={index === bannerIndex ? "is-active" : ""}
                type="button"
                key={item.id}
                onClick={() => setBannerIndex(index)}
                aria-label={`Открыть баннер ${index + 1}`}
                aria-pressed={index === bannerIndex}
              />
            ))}
          </div>
          <button type="button" aria-label="Следующий баннер" onClick={() => setBannerIndex((bannerIndex + 1) % storeState.banners.length)}><ArrowRight /></button>
        </div>
        <div className="sloi-hero__ticker" aria-hidden="true">
          <div>
            {[0, 1].map((group) => (
              <span key={group}>
                Бесплатная примерка <i>✦</i> Доставка по России <i>✦</i> Возврат 14 дней <i>✦</i> Новые бренды каждую неделю <i>✦</i>
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="sloi-categories" aria-labelledby="sloi-categories-title">
        <header>
          <p className="sloi-kicker">Навигация по гардеробу</p>
          <h2 id="sloi-categories-title" data-split>Соберите свой слой</h2>
          <p>Категории устроены как быстрые входы в ассортимент — без длинного меню и лишних уровней.</p>
        </header>
        <div className="sloi-categories__grid">
          {storeCategories.map((category, index) => (
            <button
              className={`sloi-category sloi-category--${index + 1}`}
              type="button"
              key={category.name}
              onClick={() => applyCategory(category.name)}
              data-tilt
            >
              <img src={category.image} alt="" width="700" height="820" loading="lazy" />
              <span>{String(index + 1).padStart(2, "0")}</span>
              <div>
                <strong>{category.name}</strong>
                <small>{category.description}</small>
              </div>
              <ArrowUpRight aria-hidden="true" />
            </button>
          ))}
        </div>
      </section>
        </>
      )}

      <section className="sloi-catalog" id="sloi-catalog" ref={catalogRef} aria-labelledby="sloi-catalog-title">
        <header className="sloi-catalog__header">
          <div>
            <p className="sloi-kicker">Актуальный ассортимент · {availableProducts.length} моделей</p>
            <h2 id="sloi-catalog-title" data-split>{routeTitle ?? "Каталог без компромиссов"}</h2>
          </div>
          <div className="sloi-catalog__search">
            <Search aria-hidden="true" />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Найти вещь, бренд, материал…" aria-label="Поиск по каталогу" />
            {query && <button type="button" onClick={() => setQuery("")} aria-label="Очистить поиск"><X /></button>}
          </div>
        </header>

        <div className="sloi-catalog__toolbar">
          <button className="sloi-filter-toggle" type="button" onClick={() => setFiltersOpen(!filtersOpen)} aria-expanded={filtersOpen}>
            {filtersOpen ? <PanelLeftClose /> : <SlidersHorizontal />}
            Фильтры {activeFilters > 0 && <span>{activeFilters}</span>}
          </button>
          <div className="sloi-active-filters" aria-label="Активные фильтры">
            {[...visibleCategoryFilters, ...visibleAudienceFilters, ...brands, ...sizes].map((value) => (
              <button type="button" key={value} onClick={() => {
                if (categories.includes(value)) toggle(value, categories, setCategories);
                if (audiences.includes(value)) toggle(value, audiences, setAudiences);
                if (brands.includes(value)) toggle(value, brands, setBrands);
                if (sizes.includes(value)) toggle(value, sizes, setSizes);
              }}>
                {value} <X />
              </button>
            ))}
            {activeFilters > 0 && <button className="sloi-active-filters__reset" type="button" onClick={resetFilters}>Сбросить всё</button>}
          </div>
          <label className="sloi-sort">
            <span>Сортировать</span>
            <select value={sort} onChange={(event) => setSort(event.target.value as SortMode)}>
              <option value="featured">Сначала актуальное</option>
              <option value="price-asc">Сначала дешевле</option>
              <option value="price-desc">Сначала дороже</option>
            </select>
            <ChevronDown aria-hidden="true" />
          </label>
        </div>

        <div className={`sloi-catalog__layout ${filtersOpen ? "has-filters" : ""}`}>
          <aside className="sloi-filters" aria-label="Фильтры каталога">
            <div className="sloi-filter-group">
              <h3>Категория</h3>
              {storeCategories.map((category) => (
                <label key={category.name}>
                  <input
                    type="checkbox"
                    checked={categories.includes(category.name)}
                    onChange={() => routeCategory ? onCategory(category.name) : toggle(category.name, categories, setCategories)}
                  />
                  <span>{category.name}</span>
                  <small>{categoryCounts[category.name] ?? 0}</small>
                </label>
              ))}
            </div>
            <div className="sloi-filter-group">
              <h3>Коллекция</h3>
              {["Женское", "Мужское", "Унисекс"].map((audience) => (
                <label key={audience}>
                  <input type="checkbox" checked={audiences.includes(audience)} onChange={() => {
                    if (routeAudience) {
                      onAudience(audience as StoreAudience);
                      return;
                    }
                    toggle(audience, audiences, setAudiences);
                  }} />
                  <span>{audience}</span>
                  <small>{audienceCounts[audience] ?? 0}</small>
                </label>
              ))}
            </div>
            <div className="sloi-filter-group">
              <h3>Бренд</h3>
              {allBrands.map((brand) => (
                <label key={brand}>
                  <input type="checkbox" checked={brands.includes(brand)} onChange={() => toggle(brand, brands, setBrands)} />
                  <span>{brand}</span>
                  <small>{brandCounts[brand] ?? 0}</small>
                </label>
              ))}
            </div>
            <div className="sloi-filter-group">
              <h3>Размер</h3>
              <div className="sloi-filter-sizes">
                {allSizes.map((size) => (
                  <button
                    className={sizes.includes(size) ? "is-active" : ""}
                    type="button"
                    key={size}
                    disabled={!sizeCounts[size] && !sizes.includes(size)}
                    onClick={() => toggle(size, sizes, setSizes)}
                  >
                    {size}<small>{sizeCounts[size] ?? 0}</small>
                  </button>
                ))}
              </div>
            </div>
            <div className="sloi-filter-group">
              <h3>Цена до <b>{formatMoney(maxPrice)}</b></h3>
              <input type="range" min="8000" max="40000" step="1000" value={maxPrice} onChange={(event) => setMaxPrice(Number(event.target.value))} />
              <div className="sloi-filter-range"><span>8 000 ₽</span><span>40 000 ₽</span></div>
            </div>
            <button className="sloi-secondary-button" type="button" onClick={resetFilters}>Сбросить фильтры</button>
          </aside>

          <div>
            <div className="sloi-catalog__result">
              <span>Найдено {filtered.length}</span>
              <p>Показываем только то, что есть в наличии</p>
            </div>
            {filtered.length ? (
              <div className="sloi-products">
                {filtered.map((product) => (
                  <ProductCard
                    product={product}
                    key={product.id}
                    favorite={favorites.includes(product.id)}
                    onFavorite={() => onFavorite(product.id)}
                    onOpen={() => onProduct(product)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={<Search />}
                title="По этим условиям ничего не нашли"
                text="Снимите один из фильтров или расширьте диапазон цены."
                action={<button className="sloi-secondary-button" type="button" onClick={resetFilters}>Сбросить фильтры</button>}
              />
            )}
          </div>
        </div>
      </section>

      <section className="sloi-editorial" id="sloi-journal" aria-labelledby="sloi-editorial-title">
        <div className="sloi-editorial__media">
          <img src="/assets/vanta/boutique.jpg" alt="Светлая студия с рейлом одежды из натуральных тканей" width="1300" height="900" loading="lazy" />
          <div><span>Journal / 24.07</span><span>6 минут чтения</span></div>
        </div>
        <div className="sloi-editorial__copy">
          <p className="sloi-kicker">Материалы имеют значение</p>
          <h2 id="sloi-editorial-title" data-split>Как собрать гардероб, в котором каждый слой работает</h2>
          <p>Мы отбираем вещи не по логотипу, а по конструкции: посадка, тактильность, сочетаемость и срок жизни в реальном городе.</p>
          <button type="button" onClick={() => applyCategory("Трикотаж")}>Смотреть базовые слои <ArrowUpRight /></button>
          <dl>
            <div><dt>{new Set(storeState.products.map((product) => product.brand)).size}</dt><dd>марки в каталоге</dd></div>
            <div><dt>{storeState.products.length}</dt><dd>моделей с остатками по размеру</dd></div>
            <div><dt>14 дней</dt><dd>на спокойный возврат</dd></div>
          </dl>
        </div>
      </section>

      <section className="sloi-reassurance" id="sloi-service" aria-label="Условия магазина">
        <article><Truck /><strong>Курьер или самовывоз</strong><p>Способ и точная стоимость фиксируются в заказе.</p></article>
        <article><RotateCcw /><strong>14 дней на решение</strong><p>Возврат через курьера или любой пункт.</p></article>
        <article><PackageCheck /><strong>Живые остатки SKU</strong><p>Размер списывается сервером сразу после заказа.</p></article>
        <article><RotateCcw /><strong>Безопасная песочница</strong><p>Любые изменения CMS можно вернуть одной кнопкой.</p></article>
      </section>
    </>
  );
}

function AccountView({
  storeState,
  favorites,
  onProduct,
  onShop,
  onSaveProfile,
}: {
  storeState: StoreState;
  favorites: string[];
  onProduct: (product: StoreProduct) => void;
  onShop: () => void;
  onSaveProfile: (profile: StoreState["customer"]) => Promise<void>;
}) {
  const favoriteProducts = storeState.products.filter((product) => favorites.includes(product.id));
  const [profile, setProfile] = useState(storeState.customer);
  const [saving, setSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState("");
  const completedSpend = storeState.orders
    .filter((order) => order.status !== "Отменён")
    .reduce((sum, order) => sum + order.total, 0);

  useEffect(() => setProfile(storeState.customer), [storeState.customer]);

  return (
    <section className="sloi-account" aria-labelledby="sloi-account-title">
      <header className="sloi-account__hero">
        <div>
          <p className="sloi-kicker">Личный кабинет</p>
          <h1 id="sloi-account-title">{profile.name ? `Добрый день, ${profile.name.split(" ")[0]}` : "Ваш кабинет готов"}</h1>
          <p>Заказы и контактные данные синхронизируются с общей серверной песочницей и сразу появляются в CMS.</p>
        </div>
        <div className="sloi-account__member">
          <span>SLOI / общая демо-среда</span>
          <strong>{storeState.orders.length}</strong>
          <small>{storeState.orders.length === 1 ? "заказ оформлен" : "заказов оформлено"}</small>
          <i><b style={{ width: `${Math.min(100, storeState.orders.length * 20)}%` }} /></i>
          <p>Сумма заказов: {formatMoney(completedSpend)}</p>
        </div>
      </header>

      <div className="sloi-account__grid">
        <article className="sloi-account-card sloi-account-card--orders">
          <header><div><p className="sloi-kicker">История</p><h2>Мои заказы</h2></div><Package /></header>
          <div>
            {storeState.orders.slice(0, 4).map((order) => (
              <article key={order.id}>
                <span>{order.id}<small>{formatDate(order.createdAt)}</small></span>
                <span>{order.customer}<small>{order.items.length} поз. · {order.city}</small></span>
                <span className={`sloi-status sloi-status--${order.status.replace(/\s/g, "-").toLowerCase()}`}>{order.status}</span>
                <strong>{formatMoney(order.total)}</strong>
                <small>{order.delivery === "courier" ? "Курьер" : "Самовывоз"}</small>
              </article>
            ))}
            {!storeState.orders.length && <EmptyState icon={<Package />} title="Заказов пока нет" text="Оформите заказ на витрине — он появится здесь и в CMS." />}
          </div>
        </article>

        <article className="sloi-account-card sloi-account-card--profile">
          <header><div><p className="sloi-kicker">Профиль</p><h2>Данные и адреса</h2></div><UserRound /></header>
          <form className="sloi-account-profile-form" onSubmit={async (event) => {
            event.preventDefault();
            setSaving(true);
            setProfileMessage("");
            try {
              await onSaveProfile(profile);
              setProfileMessage("Профиль сохранён на сервере");
            } catch (error) {
              setProfileMessage(error instanceof Error ? error.message : "Не удалось сохранить");
            } finally {
              setSaving(false);
            }
          }}>
            <label><span>Получатель</span><input required value={profile.name} onChange={(event) => setProfile({ ...profile, name: event.target.value })} placeholder="Имя и фамилия" /></label>
            <label><span>Email</span><input required type="email" value={profile.email} onChange={(event) => setProfile({ ...profile, email: event.target.value })} placeholder="name@example.com" /></label>
            <label><span>Телефон</span><input value={profile.phone} onChange={(event) => setProfile({ ...profile, phone: event.target.value })} placeholder="+7 999 000-00-00" /></label>
            <label><span>Город</span><input value={profile.city} onChange={(event) => setProfile({ ...profile, city: event.target.value })} placeholder="Москва" /></label>
            <label><span>Адрес</span><input value={profile.address} onChange={(event) => setProfile({ ...profile, address: event.target.value })} placeholder="Улица, дом, квартира" /></label>
            <button type="submit" disabled={saving}>{saving ? "Сохраняем…" : "Сохранить профиль"} <Check /></button>
            {profileMessage && <p role="status">{profileMessage}</p>}
          </form>
        </article>

        <article className="sloi-account-card sloi-account-card--service">
          <p className="sloi-kicker">Персональная подборка</p>
          <h2>{favoriteProducts.length ? "Соберите образ из сохранённых вещей" : "Начните персональную подборку"}</h2>
          <p>{favoriteProducts.length ? `В избранном ${favoriteProducts.length} моделей — продолжите каталогом.` : "Сохраните несколько моделей, и они появятся в этом кабинете."}</p>
          <button type="button" onClick={onShop}>Открыть каталог <ArrowRight /></button>
          <img src="/assets/vanta/look-13.jpg" alt="" width="520" height="680" />
        </article>
      </div>

      <section className="sloi-account__favorites" aria-labelledby="sloi-favorites-title">
        <header>
          <div><p className="sloi-kicker">Сохранено</p><h2 id="sloi-favorites-title">Избранное</h2></div>
          <button type="button" onClick={onShop}>Вернуться в каталог <ArrowRight /></button>
        </header>
        {favoriteProducts.length ? (
          <div>
            {favoriteProducts.map((product) => (
              <button type="button" key={product.id} onClick={() => onProduct(product)}>
                <img src={product.image} alt="" width="320" height="420" />
                <span><b>{product.name}</b><small>{formatMoney(product.price)}</small></span>
              </button>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<Heart />}
            title="Список ждёт первую вещь"
            text="Нажмите на сердце в каталоге — сохранённые модели появятся здесь."
            action={<button className="sloi-secondary-button" type="button" onClick={onShop}>Открыть каталог</button>}
          />
        )}
      </section>
    </section>
  );
}

function AnalyticsBars({ orders }: { orders: StoreOrder[] }) {
  const values = orders.slice(0, 12).reverse().map((order) => order.status === "Отменён" ? 0 : order.total);
  const chartValues = values.length ? values : [0];
  const max = Math.max(1, ...chartValues);
  return (
    <div className="sloi-analytics-bars" role="img" aria-label="Выручка по заказам общей песочницы">
      {chartValues.map((value, index) => (
        <span key={`${value}-${index}`} style={{ "--bar-value": `${Math.max(4, (value / max) * 100)}%` } as CSSProperties}>
          <i /><small>{value ? index + 1 : "—"}</small>
        </span>
      ))}
    </div>
  );
}

function AdminView({
  storeState,
  onState,
  onReset,
  onToast,
}: {
  storeState: StoreState;
  onState: (updater: (state: StoreState) => StoreState) => Promise<void>;
  onReset: () => Promise<void>;
  onToast: (message: string) => void;
}) {
  const [section, setSection] = useState<AdminSection>("overview");
  const [selectedProductId, setSelectedProductId] = useState(storeState.products[0]?.id ?? "");
  const [productQuery, setProductQuery] = useState("");
  const [draftProduct, setDraftProduct] = useState<StoreProduct | null>(storeState.products[0] ?? null);
  const [selectedBannerId, setSelectedBannerId] = useState(storeState.banners[0]?.id ?? "");
  const [draftBanner, setDraftBanner] = useState<StoreBanner | null>(storeState.banners[0] ?? null);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [editorError, setEditorError] = useState("");
  const [savingProduct, setSavingProduct] = useState(false);
  const selectedProduct = storeState.products.find((product) => product.id === selectedProductId);
  const selectedBanner = storeState.banners.find((banner) => banner.id === selectedBannerId);

  useEffect(() => {
    setDraftProduct(selectedProduct ? {
      ...selectedProduct,
      sizes: [...selectedProduct.sizes],
      variants: selectedProduct.variants.map((variant) => ({ ...variant })),
    } : null);
    setEditorError("");
  }, [selectedProductId, selectedProduct]);

  useEffect(() => {
    setDraftBanner(selectedBanner ? { ...selectedBanner } : null);
  }, [selectedBannerId, selectedBanner]);

  const revenue = storeState.orders.reduce((sum, order) => sum + order.total, 0);
  const average = Math.round(revenue / Math.max(1, storeState.orders.length));
  const lowStock = storeState.products.filter((product) => product.stock <= 7);
  const conversion = storeState.analytics.sessions
    ? (storeState.analytics.orders / storeState.analytics.sessions) * 100
    : 0;
  const filteredProducts = storeState.products.filter((product) => `${product.name} ${product.brand} ${product.sku}`.toLowerCase().includes(productQuery.toLowerCase()));

  const saveProduct = async () => {
    if (!draftProduct) return;
    const variants = draftProduct.variants
      .map((variant) => ({ size: variant.size.trim().toUpperCase(), stock: Math.max(0, Math.floor(Number(variant.stock) || 0)) }))
      .filter((variant) => variant.size);
    const uniqueSizes = new Set(variants.map((variant) => variant.size));
    if (!draftProduct.name.trim() || !draftProduct.sku.trim() || !draftProduct.brand.trim()) {
      setEditorError("Заполните название, артикул и бренд.");
      return;
    }
    if (!Number.isFinite(draftProduct.price) || draftProduct.price <= 0) {
      setEditorError("Цена должна быть больше нуля.");
      return;
    }
    if (!variants.length || uniqueSizes.size !== variants.length) {
      setEditorError("Добавьте хотя бы один размер. Названия размеров не должны повторяться.");
      return;
    }
    const normalizedProduct = {
      ...draftProduct,
      name: draftProduct.name.trim(),
      sku: draftProduct.sku.trim().toUpperCase(),
      brand: draftProduct.brand.trim(),
      variants,
      sizes: variants.map((variant) => variant.size),
      stock: variants.reduce((sum, variant) => sum + variant.stock, 0),
    };
    setSavingProduct(true);
    setEditorError("");
    try {
      await onState((state) => ({
        ...state,
        products: state.products.map((product) => product.id === normalizedProduct.id ? normalizedProduct : product),
        updatedAt: new Date().toISOString(),
      }));
      setDraftProduct(normalizedProduct);
      onToast(normalizedProduct.published ? "Товар сохранён и опубликован на витрине" : "Черновик товара сохранён");
    } finally {
      setSavingProduct(false);
    }
  };

  const createProduct = async () => {
    const product: StoreProduct = {
      id: `p-${Date.now()}`,
      sku: `SL-NEW-${storeState.products.length + 1}`,
      name: "Новая модель",
      brand: "SLOI LAB",
      category: "Верхняя одежда",
      audience: "Унисекс",
      price: 14900,
      image: "/assets/vanta/look-13.jpg",
      color: "Чёрный",
      colorHex: "#171719",
      sizes: ["S", "M", "L"],
      variants: [{ size: "S", stock: 3 }, { size: "M", stock: 4 }, { size: "L", stock: 3 }],
      material: "Укажите состав",
      stock: 10,
      rating: 5,
      reviews: 0,
      badge: "Новый",
      description: "Добавьте описание новой модели.",
      fit: "Опишите посадку модели.",
      care: "Укажите рекомендации по уходу.",
      published: false,
    };
    await onState((state) => ({ ...state, products: [product, ...state.products], updatedAt: new Date().toISOString() }));
    setSelectedProductId(product.id);
    setSection("products");
    onToast("Черновик товара создан");
  };

  const updateVariant = (index: number, patch: { size?: string; stock?: number }) => {
    if (!draftProduct) return;
    const variants = draftProduct.variants.map((variant, variantIndex) => variantIndex === index ? { ...variant, ...patch } : variant);
    setDraftProduct({
      ...draftProduct,
      variants,
      sizes: variants.map((variant) => variant.size),
      stock: variants.reduce((sum, variant) => sum + Math.max(0, Number(variant.stock) || 0), 0),
    });
    setEditorError("");
  };

  const addVariant = () => {
    if (!draftProduct) return;
    const suggestions = draftProduct.category === "Обувь"
      ? ["38", "39", "40", "41", "42", "43", "44"]
      : ["XS", "S", "M", "L", "XL", "XXL"];
    const size = suggestions.find((item) => !draftProduct.variants.some((variant) => variant.size === item)) ?? "";
    const variants = [...draftProduct.variants, { size, stock: 0 }];
    setDraftProduct({ ...draftProduct, variants, sizes: variants.map((variant) => variant.size) });
  };

  const removeVariant = (index: number) => {
    if (!draftProduct || draftProduct.variants.length <= 1) return;
    const variants = draftProduct.variants.filter((_, variantIndex) => variantIndex !== index);
    setDraftProduct({
      ...draftProduct,
      variants,
      sizes: variants.map((variant) => variant.size),
      stock: variants.reduce((sum, variant) => sum + variant.stock, 0),
    });
  };

  const deleteProduct = async () => {
    if (!draftProduct) return;
    await onState((state) => ({ ...state, products: state.products.filter((product) => product.id !== draftProduct.id), updatedAt: new Date().toISOString() }));
    const next = storeState.products.find((product) => product.id !== draftProduct.id);
    setSelectedProductId(next?.id ?? "");
    onToast("Товар удалён из каталога");
  };

  const saveBanner = async () => {
    if (!draftBanner) return;
    await onState((state) => ({
      ...state,
      banners: state.banners.map((banner) => banner.id === draftBanner.id ? draftBanner : banner),
      updatedAt: new Date().toISOString(),
    }));
    onToast("Баннер обновлён на главной");
  };

  const updateOrder = async (id: string, status: StoreOrder["status"]) => {
    await onState((state) => ({
      ...state,
      orders: state.orders.map((order) => order.id === id ? { ...order, status } : order),
      updatedAt: new Date().toISOString(),
    }));
    onToast(`Статус ${id} изменён: ${status}`);
  };

  return (
    <section className="sloi-admin" aria-labelledby="sloi-admin-title">
      <aside className="sloi-admin__nav">
        <div className="sloi-admin__brand">
          <span>S/</span>
          <div><strong>SLOI Studio</strong><small>Демо-CMS</small></div>
        </div>
        <nav aria-label="Разделы панели управления">
          <button className={section === "overview" ? "is-active" : ""} type="button" onClick={() => setSection("overview")}><LayoutDashboard /> Обзор</button>
          <button className={section === "products" ? "is-active" : ""} type="button" onClick={() => setSection("products")}><Package /> Товары <span>{storeState.products.length}</span></button>
          <button className={section === "orders" ? "is-active" : ""} type="button" onClick={() => setSection("orders")}><ShoppingBag /> Заказы <span>{storeState.orders.filter((order) => order.status === "Новый").length}</span></button>
          <button className={section === "content" ? "is-active" : ""} type="button" onClick={() => setSection("content")}><FilePenLine /> Контент</button>
        </nav>
        <div className="sloi-admin__workspace">
          <span>Рабочее пространство</span>
          <strong>Server sandbox</strong>
          <small>Все посетители работают с одним общим состоянием</small>
        </div>
        <button className="sloi-admin__reset" type="button" onClick={() => setResetConfirm(true)}><RotateCcw /> Вернуть исходную версию</button>
      </aside>

      <div className="sloi-admin__main">
        <header className="sloi-admin__topbar">
          <div>
            <p className="sloi-kicker">SLOI / управление магазином</p>
            <h1 id="sloi-admin-title">
              {section === "overview" && "Добрый день, Vivor"}
              {section === "products" && "Каталог и остатки"}
              {section === "orders" && "Заказы покупателей"}
              {section === "content" && "Витрина и баннеры"}
            </h1>
          </div>
          <div>
            <span className="sloi-admin__sync"><i /> Все изменения сохранены</span>
            <span className="sloi-admin__revision">rev. {storeState.revision}</span>
            <span className="sloi-admin__avatar">VV</span>
          </div>
        </header>

        {section === "overview" && (
          <div className="sloi-admin-dashboard">
            <section className="sloi-admin-metrics" aria-label="Основные показатели">
              <article>
                <span><CircleDollarSign /> Выручка</span>
                <strong>{formatMoney(storeState.analytics.revenue)}</strong>
                <p>Заказы общей демо-среды</p>
              </article>
              <article>
                <span><ShoppingBag /> Заказы</span>
                <strong>{storeState.orders.length}</strong>
                <p>Созданы через серверный checkout</p>
              </article>
              <article>
                <span><TrendingUp /> Средний чек</span>
                <strong>{formatMoney(average)}</strong>
                <p>Рассчитан из фактических заказов</p>
              </article>
              <article>
                <span><UsersRound /> Конверсия</span>
                <strong>{conversion.toFixed(1)}%</strong>
                <p>{storeState.analytics.orders} заказов / {storeState.analytics.sessions} сессия</p>
              </article>
            </section>

            <div className="sloi-admin-dashboard__grid">
              <article className="sloi-admin-panel sloi-admin-panel--revenue">
                <header>
                  <div><p className="sloi-kicker">Динамика</p><h2>Последние заказы</h2></div>
                  <span>{storeState.orders.length} записей</span>
                </header>
                <div className="sloi-admin-panel__revenue-value">
                  <strong>{formatMoney(storeState.analytics.revenue)}</strong><span>без отменённых заказов</span>
                </div>
                <AnalyticsBars orders={storeState.orders} />
              </article>

              <article className="sloi-admin-panel sloi-admin-panel--funnel">
                <header><div><p className="sloi-kicker">Воронка</p><h2>Путь к заказу</h2></div><BarChart3 /></header>
                <ol>
                  <li><span>Сессии</span><b>{storeState.analytics.sessions}</b><i style={{ width: "100%" }} /></li>
                  <li><span>Открыли товар</span><b>{storeState.analytics.productViews}</b><i style={{ width: `${Math.max(4, Math.min(100, storeState.analytics.productViews / Math.max(1, storeState.analytics.sessions) * 100))}%` }} /></li>
                  <li><span>Добавили в корзину</span><b>{storeState.analytics.addToCart}</b><i style={{ width: `${Math.max(4, Math.min(100, storeState.analytics.addToCart / Math.max(1, storeState.analytics.productViews) * 100))}%` }} /></li>
                  <li><span>Оформили заказ</span><b>{storeState.analytics.orders}</b><i style={{ width: `${Math.max(4, Math.min(100, storeState.analytics.orders / Math.max(1, storeState.analytics.addToCart) * 100))}%` }} /></li>
                </ol>
              </article>

              <article className="sloi-admin-panel sloi-admin-panel--orders">
                <header>
                  <div><p className="sloi-kicker">В работе</p><h2>Последние заказы</h2></div>
                  <button type="button" onClick={() => setSection("orders")}>Все заказы <ArrowRight /></button>
                </header>
                <div>
                  {storeState.orders.slice(0, 5).map((order) => (
                    <button type="button" key={order.id} onClick={() => setSection("orders")}>
                      <span><b>{order.id}</b><small>{order.customer}</small></span>
                      <span className={`sloi-status sloi-status--${order.status.replace(/\s/g, "-").toLowerCase()}`}>{order.status}</span>
                      <strong>{formatMoney(order.total)}</strong>
                      <ChevronRight />
                    </button>
                  ))}
                </div>
              </article>

              <article className="sloi-admin-panel sloi-admin-panel--stock">
                <header><div><p className="sloi-kicker">Внимание</p><h2>Заканчивается</h2></div><Package /></header>
                <div>
                  {lowStock.map((product) => (
                    <button type="button" key={product.id} onClick={() => { setSelectedProductId(product.id); setSection("products"); }}>
                      <img src={product.image} alt="" width="72" height="88" />
                      <span><b>{product.name}</b><small>{product.sku}</small></span>
                      <strong>{product.stock} шт.</strong>
                    </button>
                  ))}
                </div>
              </article>
            </div>
          </div>
        )}

        {section === "products" && (
          <div className="sloi-admin-products">
            <section className="sloi-admin-products__list">
              <header>
                <label><Search /><input value={productQuery} onChange={(event) => setProductQuery(event.target.value)} placeholder="Название или артикул" /></label>
                <button className="sloi-primary-button" type="button" onClick={createProduct}><Plus /> Добавить товар</button>
              </header>
              <div className="sloi-admin-products__table-head">
                <span>Товар</span><span>Категория</span><span>Цена</span><span>Остаток</span>
              </div>
              <div>
                {filteredProducts.map((product) => (
                  <button className={product.id === selectedProductId ? "is-active" : ""} type="button" key={product.id} onClick={() => setSelectedProductId(product.id)}>
                    <span><img src={product.image} alt="" width="72" height="88" /><i><b>{product.name}</b><small>{product.sku} · {product.brand}</small></i></span>
                    <span>{product.category}</span>
                    <strong>{formatMoney(product.price)}</strong>
                    <span className={product.stock <= 7 ? "is-low" : ""}>{product.stock} шт.</span>
                  </button>
                ))}
              </div>
            </section>

            {draftProduct && (
              <aside className="sloi-admin-editor">
                <header>
                  <div><p className="sloi-kicker">Редактирование</p><h2>{draftProduct.name}</h2></div>
                  <button type="button" aria-label="Закрыть редактор" onClick={() => setSelectedProductId("")}><X /></button>
                </header>
                <div className="sloi-admin-editor__preview">
                  <img src={draftProduct.image} alt="" width="520" height="650" />
                  <span><Eye /> Так выглядит на витрине</span>
                </div>
                <div className="sloi-admin-editor__fields">
                  <label><span>Название</span><input value={draftProduct.name} onChange={(event) => setDraftProduct({ ...draftProduct, name: event.target.value })} /></label>
                  <div>
                    <label><span>Артикул / SKU</span><input value={draftProduct.sku} onChange={(event) => setDraftProduct({ ...draftProduct, sku: event.target.value })} /></label>
                    <label><span>Бренд</span><input value={draftProduct.brand} onChange={(event) => setDraftProduct({ ...draftProduct, brand: event.target.value })} /></label>
                  </div>
                  <div>
                    <label><span>Цена, ₽</span><input type="number" inputMode="numeric" value={draftProduct.price} onChange={(event) => setDraftProduct({ ...draftProduct, price: Number(event.target.value) })} /></label>
                    <label><span>Публикация</span><select value={draftProduct.published ? "yes" : "no"} onChange={(event) => setDraftProduct({ ...draftProduct, published: event.target.value === "yes" })}><option value="yes">На витрине</option><option value="no">Скрыт</option></select></label>
                  </div>
                  <div>
                    <label><span>Категория</span><select value={draftProduct.category} onChange={(event) => setDraftProduct({ ...draftProduct, category: event.target.value as StoreProduct["category"] })}>{storeCategories.map((category) => <option key={category.name}>{category.name}</option>)}</select></label>
                    <label><span>Коллекция</span><select value={draftProduct.audience} onChange={(event) => setDraftProduct({ ...draftProduct, audience: event.target.value as StoreProduct["audience"] })}><option>Женское</option><option>Мужское</option><option>Унисекс</option></select></label>
                  </div>
                  <div>
                    <label><span>Цвет</span><input value={draftProduct.color} onChange={(event) => setDraftProduct({ ...draftProduct, color: event.target.value })} /></label>
                    <label><span>HEX цвета</span><span className="sloi-admin-editor__color"><input type="color" value={draftProduct.colorHex} onChange={(event) => setDraftProduct({ ...draftProduct, colorHex: event.target.value })} /><input value={draftProduct.colorHex} onChange={(event) => setDraftProduct({ ...draftProduct, colorHex: event.target.value })} /></span></label>
                  </div>
                  <label><span>Ссылка на изображение</span><div className="sloi-admin-editor__url"><ImageIcon /><input value={draftProduct.image} onChange={(event) => setDraftProduct({ ...draftProduct, image: event.target.value })} /></div></label>
                  <div className="sloi-image-library" role="group" aria-label="Библиотека изображений">
                    {storeImageLibrary.map((image) => (
                      <button className={draftProduct.image === image ? "is-active" : ""} type="button" key={image} onClick={() => setDraftProduct({ ...draftProduct, image })}>
                        <img src={image} alt="" width="80" height="100" loading="lazy" />
                        {draftProduct.image === image && <Check />}
                      </button>
                    ))}
                  </div>
                  <label><span>Описание</span><textarea rows={4} value={draftProduct.description} onChange={(event) => setDraftProduct({ ...draftProduct, description: event.target.value })} /></label>
                  <section className="sloi-variant-editor" aria-labelledby="sloi-variant-title">
                    <header>
                      <div><span id="sloi-variant-title">Размеры и остатки SKU</span><small>{draftProduct.stock} шт. суммарно</small></div>
                      <button type="button" onClick={addVariant}><Plus /> Добавить размер</button>
                    </header>
                    <div className="sloi-variant-editor__labels"><span>Размер</span><span>Остаток</span><span /></div>
                    <div className="sloi-variant-editor__rows">
                      {draftProduct.variants.map((variant, index) => (
                        <div className="sloi-variant-row" key={`${index}-${variant.size}`}>
                          <input aria-label={`Размер ${index + 1}`} value={variant.size} placeholder="Напр. M" onChange={(event) => updateVariant(index, { size: event.target.value })} />
                          <label>
                            <button type="button" aria-label="Уменьшить остаток" onClick={() => updateVariant(index, { stock: Math.max(0, variant.stock - 1) })}><Minus /></button>
                            <input aria-label={`Остаток размера ${variant.size || index + 1}`} type="number" min="0" step="1" inputMode="numeric" value={variant.stock} onChange={(event) => updateVariant(index, { stock: Math.max(0, Number(event.target.value) || 0) })} />
                            <button type="button" aria-label="Увеличить остаток" onClick={() => updateVariant(index, { stock: variant.stock + 1 })}><Plus /></button>
                          </label>
                          <button type="button" disabled={draftProduct.variants.length <= 1} aria-label={`Удалить размер ${variant.size || index + 1}`} onClick={() => removeVariant(index)}><Trash2 /></button>
                        </div>
                      ))}
                    </div>
                  </section>
                  <label><span>Состав</span><input value={draftProduct.material} onChange={(event) => setDraftProduct({ ...draftProduct, material: event.target.value })} /></label>
                  <label><span>Посадка</span><textarea rows={2} value={draftProduct.fit} onChange={(event) => setDraftProduct({ ...draftProduct, fit: event.target.value })} /></label>
                  <label><span>Уход</span><textarea rows={2} value={draftProduct.care} onChange={(event) => setDraftProduct({ ...draftProduct, care: event.target.value })} /></label>
                  {editorError && <p className="sloi-admin-editor__error" role="alert">{editorError}</p>}
                </div>
                <footer>
                  <button className="sloi-admin-editor__delete" type="button" onClick={deleteProduct}><Trash2 /> Удалить</button>
                  <button className="sloi-primary-button" disabled={savingProduct} type="button" onClick={saveProduct}>
                    {savingProduct ? "Сохраняем…" : draftProduct.published ? "Сохранить на витрине" : "Сохранить черновик"} <Check />
                  </button>
                </footer>
              </aside>
            )}
          </div>
        )}

        {section === "orders" && (
          <div className="sloi-admin-orders">
            <section className="sloi-admin-orders__summary">
              <article><span>Новые</span><strong>{storeState.orders.filter((order) => order.status === "Новый").length}</strong><small>требуют подтверждения</small></article>
              <article><span>В работе</span><strong>{storeState.orders.filter((order) => ["Подтверждён", "Собирается", "Передан в доставку"].includes(order.status)).length}</strong><small>на разных этапах</small></article>
              <article><span>Сегодня</span><strong>{formatMoney(storeState.orders.slice(0, 2).reduce((sum, order) => sum + order.total, 0))}</strong><small>выручка демо</small></article>
            </section>
            <section className="sloi-admin-orders__table">
              <header><span>Заказ</span><span>Покупатель</span><span>Состав</span><span>Источник</span><span>Сумма</span><span>Статус</span></header>
              <div>
                {storeState.orders.map((order) => (
                  <article key={order.id}>
                    <span><b>{order.id}</b><small>{formatDate(order.createdAt)}</small></span>
                    <span><b>{order.customer}</b><small>{order.email} · {order.city}</small></span>
                    <span>
                      <i>{order.items.slice(0, 3).map((item) => {
                        const product = storeState.products.find((entry) => entry.id === item.productId);
                        return product ? <img key={item.productId} src={product.image} alt="" width="42" height="52" /> : null;
                      })}</i>
                      <small>{order.items.length} позиции</small>
                    </span>
                    <span>{order.source}</span>
                    <strong>{formatMoney(order.total)}</strong>
                    <label>
                      <select value={order.status} onChange={(event) => updateOrder(order.id, event.target.value as StoreOrder["status"])}>
                        {orderStatuses.map((status) => <option key={status}>{status}</option>)}
                      </select>
                      <ChevronDown />
                    </label>
                  </article>
                ))}
              </div>
            </section>
          </div>
        )}

        {section === "content" && (
          <div className="sloi-admin-content">
            <section className="sloi-admin-content__nav">
              <header><p className="sloi-kicker">Главная страница</p><h2>Баннер-карусель</h2><span>Изменения появляются на витрине после сохранения.</span></header>
              <div>
                {storeState.banners.map((banner, index) => (
                  <button className={banner.id === selectedBannerId ? "is-active" : ""} type="button" key={banner.id} onClick={() => setSelectedBannerId(banner.id)}>
                    <img src={banner.image} alt="" width="100" height="70" />
                    <span><small>Слайд {index + 1}</small><b>{banner.title}</b></span>
                    <ChevronRight />
                  </button>
                ))}
              </div>
            </section>
            {draftBanner && (
              <section className="sloi-admin-content__editor">
                <div className={`sloi-admin-content__preview sloi-admin-content__preview--${draftBanner.tone}`}>
                  <img src={draftBanner.image} alt="" width="1100" height="720" />
                  <div><span>{draftBanner.eyebrow}</span><strong>{draftBanner.title}</strong><p>{draftBanner.text}</p><b>{draftBanner.cta} <ArrowRight /></b></div>
                </div>
                <div className="sloi-admin-content__fields">
                  <label><span>Надзаголовок</span><input value={draftBanner.eyebrow} onChange={(event) => setDraftBanner({ ...draftBanner, eyebrow: event.target.value })} /></label>
                  <label><span>Заголовок</span><input value={draftBanner.title} onChange={(event) => setDraftBanner({ ...draftBanner, title: event.target.value })} /></label>
                  <label><span>Описание</span><textarea rows={3} value={draftBanner.text} onChange={(event) => setDraftBanner({ ...draftBanner, text: event.target.value })} /></label>
                  <label><span>Текст кнопки</span><input value={draftBanner.cta} onChange={(event) => setDraftBanner({ ...draftBanner, cta: event.target.value })} /></label>
                  <label><span>Ссылка на изображение</span><div><ImageIcon /><input value={draftBanner.image} onChange={(event) => setDraftBanner({ ...draftBanner, image: event.target.value })} /></div></label>
                  <fieldset><legend>Контраст текста</legend><button className={draftBanner.tone === "paper" ? "is-active" : ""} type="button" onClick={() => setDraftBanner({ ...draftBanner, tone: "paper" })}>Светлый</button><button className={draftBanner.tone === "ink" ? "is-active" : ""} type="button" onClick={() => setDraftBanner({ ...draftBanner, tone: "ink" })}>Тёмный</button></fieldset>
                  <button className="sloi-primary-button" type="button" onClick={saveBanner}>Сохранить баннер <Check /></button>
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {resetConfirm && (
        <div className="sloi-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setResetConfirm(false)}>
          <section className="sloi-reset-dialog" role="alertdialog" aria-modal="true" aria-labelledby="reset-title" aria-describedby="reset-text">
            <span><RotateCcw /></span>
            <p className="sloi-kicker">Сброс демо</p>
            <h2 id="reset-title">Вернуть магазин к исходному состоянию?</h2>
            <p id="reset-text">Все общие заказы, изменения товаров, SKU-остатков, профиля и баннеров будут удалены для всех посетителей.</p>
            <div>
              <button className="sloi-secondary-button" type="button" onClick={() => setResetConfirm(false)}>Отмена</button>
              <button className="sloi-danger-button" type="button" onClick={() => { onReset(); setResetConfirm(false); }}>Да, вернуть исходную версию</button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

export function SloiStorePage() {
  const root = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState<StoreView>("shop");
  const [storeState, setStoreState] = useState<StoreState>(emptyStoreState);
  const [cart, setCart] = useState<CartLine[]>(() => readJson(CART_KEY, []));
  const [favorites, setFavorites] = useState<string[]>(() => readJson(FAVORITES_KEY, []));
  const [selectedProduct, setSelectedProduct] = useState<StoreProduct | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [headerSearch, setHeaderSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState("");
  const [sandboxId, setSandboxId] = useState("");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState("");
  const categorySlug = location.pathname.match(/^\/sloi-store\/category\/([^/]+)\/?$/)?.[1];
  const audienceSlug = location.pathname.match(/^\/sloi-store\/collection\/([^/]+)\/?$/)?.[1];
  const activeCategoryRoute = categoryRoutes.find((route) => route.slug === categorySlug);
  const activeAudienceRoute = audienceRoutes.find((route) => route.slug === audienceSlug);
  const routeImage = activeCategoryRoute
    ? storeCategories.find((category) => category.name === activeCategoryRoute.name)?.image
    : activeAudienceRoute?.name === "Женское"
      ? "/assets/vanta/look-06.jpg"
      : activeAudienceRoute?.name === "Мужское"
        ? "/assets/vanta/look-09.jpg"
        : "/assets/vanta/look-01.jpg";

  const loadStore = async () => {
    setLoading(true);
    setApiError("");
    try {
      const data = await sloiApi.bootstrap();
      setStoreState(data);
      setSandboxId(data.sandboxId);
      setCart((items) => items.filter((line) => {
        const product = data.products.find((entry) => entry.id === line.productId);
        const variant = product?.variants.find((entry) => entry.size === line.size);
        return Boolean(variant?.stock);
      }));
    } catch (error) {
      setApiError(error instanceof Error ? error.message : "Магазин временно недоступен");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadStore();
  }, []);

  useEffect(() => {
    const nextView: StoreView = location.pathname.startsWith("/sloi-store/admin")
      ? "admin"
      : location.pathname.startsWith("/sloi-store/account")
        ? "account"
        : "shop";
    setView(nextView);
    setMobileNavOpen(false);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    window.localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 3300);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    const hasOverlay = Boolean(selectedProduct || cartOpen || checkoutOpen);
    document.body.style.overflow = hasOverlay ? "hidden" : "";
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setSelectedProduct(null);
      setCartOpen(false);
      setCheckoutOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [cartOpen, checkoutOpen, selectedProduct]);

  useGsap(root, (gsapInstance) => {
    gsapInstance
      .timeline()
      .from(".sloi-header", { y: -24, autoAlpha: 0, duration: 0.75, ease: "power3.out" })
      .from(".sloi-view-switcher", { y: 16, autoAlpha: 0, duration: 0.55 }, 0.18);

    if (view === "shop") {
      gsapInstance.from(".sloi-hero__copy > *", { y: 28, autoAlpha: 0, duration: 0.8, stagger: 0.07, ease: "power3.out", delay: 0.12 });
      gsapInstance.from(".sloi-hero__media", { scale: 1.08, autoAlpha: 0, duration: 1.2, ease: "expo.out" });
      gsapInstance.from(".sloi-category", {
        y: 60,
        autoAlpha: 0,
        stagger: 0.08,
        duration: 0.85,
        ease: "power3.out",
        scrollTrigger: { trigger: ".sloi-categories__grid", start: "top 82%", once: true },
      });
      gsapInstance.utils.toArray<HTMLElement>(".sloi-product").forEach((card) => {
        gsapInstance.from(card, {
          y: 44,
          autoAlpha: 0,
          duration: 0.72,
          ease: "power3.out",
          scrollTrigger: { trigger: card, start: "top 92%", once: true },
        });
      });
    } else {
      gsapInstance.from(".sloi-account > *, .sloi-admin__main > *", { y: 24, autoAlpha: 0, stagger: 0.06, duration: 0.68, ease: "power3.out" });
    }
  }, [view, loading]);

  const updateState = async (updater: (state: StoreState) => StoreState) => {
    const previous = storeState;
    const next = updater(previous);
    setStoreState(next);
    try {
      const result = await sloiApi.publishSnapshot(next);
      if (result.state) setStoreState(result.state);
    } catch (error) {
      setStoreState(previous);
      showToast(error instanceof Error ? error.message : "Изменения не сохранены");
      throw error;
    }
  };

  const showToast = (message: string) => {
    setToast("");
    window.requestAnimationFrame(() => setToast(message));
  };

  const toggleFavorite = (id: string) => {
    setFavorites((items) => items.includes(id) ? items.filter((item) => item !== id) : [...items, id]);
    showToast(favorites.includes(id) ? "Удалено из избранного" : "Сохранено в избранное");
  };

  const addToCart = (product: StoreProduct, size: string) => {
    const available = product.variants.find((variant) => variant.size === size)?.stock ?? 0;
    setCart((items) => {
      const index = items.findIndex((item) => item.productId === product.id && item.size === size);
      const nextQuantity = index === -1 ? 1 : items[index].quantity + 1;
      if (nextQuantity > available) {
        showToast(`В размере ${size} доступно только ${available} шт.`);
        return items;
      }
      if (index === -1) return [...items, { productId: product.id, size, quantity: 1 }];
      return items.map((item, itemIndex) => itemIndex === index ? { ...item, quantity: item.quantity + 1 } : item);
    });
    void sloiApi.event("add_to_cart", product.id).then((result) => {
      if (result.analytics) setStoreState((state) => ({ ...state, analytics: result.analytics! }));
    }).catch(() => undefined);
    setSelectedProduct(null);
    setCartOpen(true);
    showToast(`${product.name} добавлен в корзину`);
  };

  const submitOrder = async (payload: {
    customer: { name: string; email: string; phone: string; city: string; address: string };
    delivery: "courier" | "pickup";
    payment: "card" | "on-receipt";
  }) => {
    try {
      const result = await sloiApi.createOrder({ ...payload, items: cart });
      if (result.state) setStoreState(result.state);
      const createdOrder = result.order as StoreOrder | undefined;
      setCart([]);
      setCheckoutOpen(false);
      setCartOpen(false);
      setView("account");
      navigate("/sloi-store/account");
      window.scrollTo({ top: 0, behavior: "smooth" });
      showToast(`Заказ ${createdOrder?.id ?? ""} создан и уже виден в CMS`);
    } catch (error) {
      const message = error instanceof StoreApiError ? error.message : "Заказ не создан";
      showToast(message);
      if (error instanceof StoreApiError && error.code === "OUT_OF_STOCK") void loadStore();
      throw error;
    }
  };

  const resetStore = async () => {
    try {
      const initial = await sloiApi.reset();
      setStoreState(initial);
      setCart([]);
      setFavorites([]);
      window.localStorage.removeItem(CART_KEY);
      window.localStorage.removeItem(FAVORITES_KEY);
      showToast("Общая песочница восстановлена");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Сброс не выполнен");
    }
  };

  const saveProfile = async (profile: StoreState["customer"]) => {
    const result = await sloiApi.saveProfile(profile);
    if (result.state) setStoreState(result.state);
  };

  const switchView = (next: StoreView) => {
    setView(next);
    setMobileNavOpen(false);
    navigate(next === "shop" ? "/sloi-store" : `/sloi-store/${next}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openCatalog = (detail: { audience?: string; query?: string; reset?: boolean } = {}) => {
    setView("shop");
    setMobileNavOpen(false);
    if (detail.audience) {
      const route = audienceRoutes.find((item) => item.name === detail.audience);
      navigate(route ? `/sloi-store/collection/${route.slug}` : "/sloi-store");
      return;
    }
    navigate("/sloi-store");
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("sloi:catalog", { detail }));
    }, 0);
  };

  const openJournal = () => {
    setView("shop");
    setMobileNavOpen(false);
    navigate("/sloi-store");
    window.setTimeout(() => {
      document.querySelector(".sloi-editorial")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const openProduct = (product: StoreProduct) => {
    setSelectedProduct(product);
    void sloiApi.event("product_view", product.id).then((result) => {
      if (result.analytics) setStoreState((state) => ({ ...state, analytics: result.analytics! }));
    }).catch(() => undefined);
  };

  if (loading) {
    return (
      <main id="main" className="sloi-page sloi-api-state" aria-busy="true" data-motion-ready="true">
        <span className="sloi-api-state__mark">S/</span>
        <p className="sloi-kicker">Подключаем серверную витрину</p>
        <h1>Собираем каталог и остатки</h1>
        <i />
      </main>
    );
  }

  if (apiError) {
    return (
      <main id="main" className="sloi-page sloi-api-state" data-motion-ready="true">
        <span className="sloi-api-state__mark">!</span>
        <p className="sloi-kicker">SLOI / ошибка соединения</p>
        <h1>Магазин не загрузился</h1>
        <p>{apiError}</p>
        <button className="sloi-primary-button" type="button" onClick={() => void loadStore()}>Попробовать снова</button>
      </main>
    );
  }

  return (
    <main id="main" ref={root} className={`sloi-page sloi-page--${view}`} data-view={view} data-motion-ready="true">
      <header className="sloi-header">
        <button className="sloi-header__menu" type="button" aria-label="Открыть меню" onClick={() => setMobileNavOpen(!mobileNavOpen)}><Menu /></button>
        <button className="sloi-logo" type="button" onClick={() => switchView("shop")} aria-label="SLOI — на главную">
          <span>SLOI</span><i>multibrand / concept store</i>
        </button>
        <nav className={mobileNavOpen ? "is-open" : ""} aria-label="Разделы магазина">
          <button type="button" onClick={() => openCatalog({ reset: true })}>Новинки</button>
          <button type="button" onClick={() => openCatalog({ audience: "Женское" })}>Женское</button>
          <button type="button" onClick={() => openCatalog({ audience: "Мужское" })}>Мужское</button>
          <button type="button" onClick={() => openCatalog({ query: "SLOI LAB", reset: true })}>SLOI LAB</button>
          <button type="button" onClick={openJournal}>Journal</button>
        </nav>
        <div className="sloi-header__search">
          <Search aria-hidden="true" />
          <input
            value={headerSearch}
            onChange={(event) => {
              setHeaderSearch(event.target.value);
              if (view !== "shop") openCatalog({ query: event.target.value });
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") openCatalog({ query: headerSearch });
            }}
            placeholder="Поиск"
            aria-label="Поиск по магазину"
          />
          {headerSearch && <span>{storeState.products.filter((product) => `${product.name} ${product.brand}`.toLowerCase().includes(headerSearch.toLowerCase())).length} результатов</span>}
        </div>
        <div className="sloi-header__actions">
          <button type="button" onClick={() => switchView("account")} aria-label="Открыть личный кабинет"><UserRound /></button>
          <button type="button" onClick={() => switchView("account")} aria-label="Открыть избранное"><Heart /><span>{favorites.length}</span></button>
          <button type="button" onClick={() => setCartOpen(true)} aria-label={`Открыть корзину, товаров: ${cartCount}`}><ShoppingBag /><span>{cartCount}</span></button>
        </div>
      </header>

      <div className="sloi-view-switcher" aria-label="Режим просмотра">
        <span>Песочница {sandboxId}</span>
        <button className={view === "shop" ? "is-active" : ""} type="button" onClick={() => switchView("shop")}><Store /> Витрина</button>
        <button className={view === "account" ? "is-active" : ""} type="button" onClick={() => switchView("account")}><UserRound /> Кабинет</button>
        <button className={view === "admin" ? "is-active" : ""} type="button" onClick={() => switchView("admin")}><Settings2 /> Studio CMS <i>Демо</i></button>
      </div>

      {view === "shop" && (
        <Storefront
          storeState={storeState}
          favorites={favorites}
          onFavorite={toggleFavorite}
          onProduct={openProduct}
          onCategory={(category) => {
            const route = categoryRoutes.find((item) => item.name === category);
            navigate(route ? `/sloi-store/category/${route.slug}` : "/sloi-store");
          }}
          onAudience={(audience) => {
            const route = audienceRoutes.find((item) => item.name === audience);
            navigate(route ? `/sloi-store/collection/${route.slug}` : "/sloi-store");
          }}
          onHome={() => navigate("/sloi-store")}
          routeCategory={activeCategoryRoute?.name}
          routeAudience={activeAudienceRoute?.name}
          routeTitle={activeCategoryRoute?.name ?? activeAudienceRoute?.title}
          routeDescription={activeCategoryRoute?.description ?? (activeAudienceRoute ? `Независимые марки и выразительные силуэты в коллекции «${activeAudienceRoute.name}». Фильтры учитывают реальные остатки каждого размера.` : undefined)}
          routeImage={routeImage}
        />
      )}
      {view === "account" && (
        <AccountView
          storeState={storeState}
          favorites={favorites}
          onProduct={openProduct}
          onShop={() => switchView("shop")}
          onSaveProfile={saveProfile}
        />
      )}
      {view === "admin" && (
        <AdminView
          storeState={storeState}
          onState={updateState}
          onReset={resetStore}
          onToast={showToast}
        />
      )}

      {view !== "admin" && (
        <footer className="sloi-footer">
          <div>
            <strong>SLOI</strong>
            <p>Независимые марки и вещи с продуманной конструкцией.</p>
          </div>
          <div><span>Покупателям</span><a href="#sloi-service">Доставка и оплата</a><a href="#sloi-service">Возврат</a><a href="#sloi-catalog">Размеры и наличие</a></div>
          <div><span>О магазине</span><a href="#sloi-catalog">Каталог</a><a href="#sloi-journal">Journal</a><a href="mailto:info@vivor.tech">Контакты</a></div>
          <div><span>Узнавать первым</span><p>{newsletterStatus || "Дропы и редакционные подборки — без ежедневного спама."}</p><label><input type="email" value={newsletterEmail} onChange={(event) => setNewsletterEmail(event.target.value)} placeholder="Email" aria-label="Email для подписки" /><button type="button" aria-label="Подписаться" onClick={async () => {
            setNewsletterStatus("");
            try {
              await sloiApi.subscribe(newsletterEmail);
              setNewsletterStatus("Готово — адрес сохранён в общей песочнице");
              setNewsletterEmail("");
            } catch (error) {
              setNewsletterStatus(error instanceof Error ? error.message : "Не удалось подписаться");
            }
          }}><ArrowRight /></button></label></div>
          <p>© 2026 SLOI concept store · Москва · Сделано как интерактивный проект Vivor.tech</p>
        </footer>
      )}

      {selectedProduct && (
        <ProductDrawer
          product={selectedProduct}
          favorite={favorites.includes(selectedProduct.id)}
          onClose={() => setSelectedProduct(null)}
          onFavorite={() => toggleFavorite(selectedProduct.id)}
          onAdd={(size) => addToCart(selectedProduct, size)}
        />
      )}
      {cartOpen && !checkoutOpen && (
        <CartDrawer
          products={storeState.products}
          cart={cart}
          onClose={() => setCartOpen(false)}
          onQuantity={(index, next) => {
            if (next < 1) setCart((items) => items.filter((_, itemIndex) => itemIndex !== index));
            else setCart((items) => items.map((item, itemIndex) => {
              if (itemIndex !== index) return item;
              const product = storeState.products.find((entry) => entry.id === item.productId);
              const available = product?.variants.find((variant) => variant.size === item.size)?.stock ?? 0;
              if (next > available) {
                showToast(`В размере ${item.size} доступно только ${available} шт.`);
                return item;
              }
              return { ...item, quantity: next };
            }));
          }}
          onRemove={(index) => setCart((items) => items.filter((_, itemIndex) => itemIndex !== index))}
          onCheckout={() => {
            if (!cart.length) return;
            void sloiApi.event("checkout_start").then((result) => {
              if (result.analytics) setStoreState((state) => ({ ...state, analytics: result.analytics! }));
            }).catch(() => undefined);
            setCartOpen(false);
            setCheckoutOpen(true);
          }}
        />
      )}
      {checkoutOpen && (
        <CheckoutDrawer
          products={storeState.products}
          cart={cart}
          onClose={() => setCheckoutOpen(false)}
          onSubmit={submitOrder}
        />
      )}

      {toast && <div className="sloi-toast" role="status" aria-live="polite"><Check /> {toast}</div>}
      <ConceptBar label="Интернет-магазин · CMS · кабинет" dark={view === "admin"} />
    </main>
  );
}
